import { getCookie } from "./cookieUtils";

/** Query param that enables edge cookie-domain hints and anon-ID opt-out gating (DomCapture=true). */
export const DOM_CAPTURE_QUERY_PARAM = "DomCapture";

/** Set by edge viewer-response when DomCapture=true; must match 2u-edge TWO_U_TAGULAR_COOKIE_DOMAIN. */
export const TWO_U_TAGULAR_COOKIE_DOMAIN = "two_u_tagular_cookie_domain";

export function isDomCaptureEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(DOM_CAPTURE_QUERY_PARAM) === "true";
}

/**
 * Values from edge getCookieDomain: optional leading dot, DNS-like labels only.
 * Rejects delimiter injection (e.g. %3B / ;) before Tagular uses cookieDomain.
 */
export const TAGULAR_COOKIE_DOMAIN_PATTERN =
  /^\.?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export function isValidTagularCookieDomain(value: string): boolean {
  return value.length > 0 && value.length <= 253 && TAGULAR_COOKIE_DOMAIN_PATTERN.test(value);
}

/**
 * Tagular cookieDomain for client-side code (e.g. validateAnonId).
 * DomCapture=true: read domain from edge hint cookie (validated; safe fallback).
 * Otherwise: hostname with info. stripped (legacy / local dev without edge).
 */
export function getTagularCookieDomain(): string {
  const hostnameFallback = () => location.hostname.replace(/^info\./, ".");

  if (!isDomCaptureEnabled()) {
    return hostnameFallback();
  }

  const hint = getCookie(TWO_U_TAGULAR_COOKIE_DOMAIN);
  if (!hint) return hostnameFallback();

  try {
    const decoded = decodeURIComponent(hint);
    return isValidTagularCookieDomain(decoded) ? decoded : hostnameFallback();
  } catch {
    return hostnameFallback();
  }
}

/**
 * Tagular cookieDomain for cohesion bootstrap scripts.
 * DomCapture=true: read domain from edge hint cookie (validated; safe fallback).
 * Otherwise: hostname with info. stripped (legacy / local dev without edge).
 */
export const TAGULAR_COOKIE_DOMAIN_JS = `(function() {
  var hostnameFallback = function() {
    return location.hostname.replace(/^info\\./, ".");
  };
  var domainPattern = /^\\.?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
  var domCapture = new URLSearchParams(location.search).get("${DOM_CAPTURE_QUERY_PARAM}") === "true";
  if (!domCapture) return hostnameFallback();
  var name = "${TWO_U_TAGULAR_COOKIE_DOMAIN}";
  var m = document.cookie.match(new RegExp("(?:^|;\\\\s*)" + name + "=([^;]*)"));
  if (!m) return hostnameFallback();
  var decoded;
  try {
    decoded = decodeURIComponent(m[1]);
  } catch (e) {
    return hostnameFallback();
  }
  if (decoded.length > 0 && decoded.length <= 253 && domainPattern.test(decoded)) {
    return decoded;
  }
  return hostnameFallback();
})()`;
